import React, { useCallback, useRef } from 'react';
import { Checkbox, MenuItem, Popover, TableCell } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Sorter from 'components/common/Sorter';
import { getTablePreferences, setTablePreferences } from 'components/utils';
import { Item } from '../DataGrid';
import ColumnResizer from './ColumnResizer';
import {
  applySavedColumnsState,
  compareDataGridColumnsBySavedOrder,
  DataGridColumn,
  getLeadingStickyFallbackWidth,
  isManageableColumnName,
  reorderManageableColumnsByIndex,
  SavedColumn,
  stickyCellSx,
  toSavedVisibleColumns,
} from './utils';
import RequireRoles from 'components/common/requireRole';

type RenderColumnsProps<T extends Item> = {
  tableName: string;
  columns: DataGridColumn[];
  list: T[];
  selectedObjects: T[];
  setSelectedObjects: (objects: T[]) => void;
  fetchList: () => void;
  columnWidths: Record<string, number>;
  setColumnWidths: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  onColumnsChange?: () => void;
};

export const getVisibleColumns = ({ tableName, columns }: { tableName: string; columns: DataGridColumn[] }) => {
  const tablePreferences = getTablePreferences(tableName);
  const savedColumns = (tablePreferences.columns || []) as SavedColumn[];
  const savedColumnsLength = savedColumns.length;

  const filteredColumns = columns.filter(column => {
    if (savedColumnsLength === 0) {
      return column.isVisible;
    }

    const savedColumn = savedColumns.find(saved => saved.name === column.name);
    return savedColumn ? savedColumn.isVisible !== false : false;
  });

  if (savedColumnsLength === 0) {
    return filteredColumns;
  }

  return [...filteredColumns].sort((a, b) => compareDataGridColumnsBySavedOrder(a, b, savedColumns, columns));
};

function RenderColumns<T extends Item>({
  tableName,
  columns,
  list,
  selectedObjects,
  setSelectedObjects,
  fetchList,
  columnWidths,
  setColumnWidths,
  onColumnsChange,
}: RenderColumnsProps<T>) {
  const visibleColumns = getVisibleColumns({ tableName, columns });
  const saveWidthTimeoutRef = useRef<Record<string, NodeJS.Timeout>>({});
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<HTMLElement | null>(null);
  const [selectedColumnName, setSelectedColumnName] = React.useState<string | null>(null);

  const getColumnsStateFromPreferences = useCallback(() => {
    const savedColumns = (getTablePreferences(tableName).columns || []) as SavedColumn[];
    return applySavedColumnsState({ columns, savedColumns });
  }, [tableName, columns]);

  const persistColumns = useCallback(
    (updatedColumns: DataGridColumn[]) => {
      setTablePreferences({
        tableName,
        values: [
          {
            key: 'columns',
            value: toSavedVisibleColumns(updatedColumns),
          },
        ],
      });
      onColumnsChange?.();
      fetchList();
    },
    [tableName, fetchList, onColumnsChange],
  );

  const updateColumnWidth = useCallback(
    (columnName: string, width: number) => {
      setColumnWidths(prev => ({ ...prev, [columnName]: width }));

      if (saveWidthTimeoutRef.current[columnName]) {
        clearTimeout(saveWidthTimeoutRef.current[columnName]);
      }

      saveWidthTimeoutRef.current[columnName] = setTimeout(() => {
        const tablePreferences = getTablePreferences(tableName);
        const raw = tablePreferences.columns as SavedColumn[] | undefined;
        const savedColumns = Array.isArray(raw) && raw.length > 0 ? raw : toSavedVisibleColumns(columns);

        for (const column of savedColumns) {
          if (column.name === columnName) {
            column.width = width;
          }
        }

        setTablePreferences({
          tableName,
          values: [{ key: 'columns', value: savedColumns }],
        });
        onColumnsChange?.();

        delete saveWidthTimeoutRef.current[columnName];
      }, 300);
    },
    [tableName, columns, setColumnWidths, onColumnsChange],
  );

  const renderHeader = (column: DataGridColumn, index: number) => {
    const tablePreferences = getTablePreferences(tableName);
    const savedColumns = (tablePreferences.columns || columns) as DataGridColumn[];
    const savedColumn = savedColumns.find(c => c.name === column.name);
    const fallbackLeadingWidth = getLeadingStickyFallbackWidth({ column, visibleColumns });
    const columnWidth = columnWidths[column.name!] ?? savedColumn?.width ?? column.width ?? fallbackLeadingWidth;

    if (column.isRowSelector) {
      return (
        <TableCell
          key={index}
          sx={{
            ...(column.sx || {}),
            ...(columnWidth && { width: `${columnWidth}px`, minWidth: `${columnWidth}px`, maxWidth: `${columnWidth}px` }),
            ...stickyCellSx({}),
            backgroundColor: 'background.paper',
          }}
        >
          <Checkbox
            checked={selectedObjects.length > 0 && selectedObjects.length === list.length}
            onChange={e => {
              setSelectedObjects(e.target.checked ? list : []);
            }}
            sx={{ m: 0, p: 0, ml: '5px' }}
          />
        </TableCell>
      );
    }

    const cellSx = {
      ...(column.sx || {}),
      whiteSpace: 'nowrap',
      position: 'relative' as const,
      ...(columnWidth && { width: `${columnWidth}px`, minWidth: `${columnWidth}px`, maxWidth: `${columnWidth}px` }),
      ...(column.isSticky && { ...stickyCellSx({ left: column.stickyLeft }) }),
      backgroundColor: 'background.paper',
    };

    const isManageableColumn = isManageableColumnName(column.name);

    if (column.hasSorter && column.name && column.title) {
      const columnName = column.name;

      return (
        <TableCell
          key={index}
          sx={cellSx}
          onClick={event => {
            if (!isManageableColumn) {
              return;
            }
            setSelectedColumnName(columnName);
            setMenuAnchorEl(event.currentTarget);
          }}
        >
          <Sorter
            tableName={tableName}
            title={column.title}
            columnName={columnName}
            onSort={() => {
              fetchList();
            }}
          />
          <ColumnResizer
            initialWidth={columnWidth}
            minWidth={column.minWidth}
            onResize={width => {
              updateColumnWidth(columnName, width);
            }}
          />
        </TableCell>
      );
    }

    const columnName = column.name;

    return (
      <TableCell
        key={index}
        sx={cellSx}
        onClick={event => {
          if (!isManageableColumn) {
            return;
          }
          if (!columnName) {
            return;
          }

          setSelectedColumnName(columnName);
          setMenuAnchorEl(event.currentTarget);
        }}
      >
        {column.title}
        {columnName && (
          <ColumnResizer
            initialWidth={columnWidth}
            minWidth={column.minWidth}
            onResize={width => {
              updateColumnWidth(columnName, width);
            }}
          />
        )}
      </TableCell>
    );
  };

  const menuOpen = Boolean(menuAnchorEl && selectedColumnName);
  const manageableVisibleColumns = visibleColumns.filter(col => isManageableColumnName(col.name));
  const selectedColumnIndex = manageableVisibleColumns.findIndex(col => col.name === selectedColumnName);

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setSelectedColumnName(null);
  };

  const moveSelectedColumn = (direction: 'left' | 'right') => {
    if (!selectedColumnName) {
      return;
    }

    const fullColumns = getColumnsStateFromPreferences();
    const reorderable = fullColumns.filter(col => isManageableColumnName(col.name));
    const visibleReorderable = reorderable.filter(col => col.isVisible);
    const currentVisibleIndex = visibleReorderable.findIndex(col => col.name === selectedColumnName);

    if (currentVisibleIndex < 0) {
      return;
    }

    const targetVisibleIndex = direction === 'left' ? currentVisibleIndex - 1 : currentVisibleIndex + 1;

    if (targetVisibleIndex < 0 || targetVisibleIndex >= visibleReorderable.length) {
      return;
    }

    const currentIndex = reorderable.findIndex(col => col.name === selectedColumnName);
    const targetColumnName = visibleReorderable[targetVisibleIndex].name;
    const targetIndex = reorderable.findIndex(col => col.name === targetColumnName);

    if (currentIndex < 0 || targetIndex < 0) {
      return;
    }

    const updatedColumns = reorderManageableColumnsByIndex({
      columns: fullColumns,
      sourceIndex: currentIndex,
      targetIndex,
    });

    persistColumns(updatedColumns);
    handleCloseMenu();
  };

  const hideSelectedColumn = () => {
    if (!selectedColumnName) {
      return;
    }

    const fullColumns = getColumnsStateFromPreferences();
    const updatedColumns = fullColumns.map(col => {
      if (col.name === selectedColumnName) {
        return { ...col, isVisible: false };
      }
      return col;
    });

    persistColumns(updatedColumns);
    handleCloseMenu();
  };

  return (
    <>
      {visibleColumns.map((column, index) => (
        <React.Fragment key={column.name || `header-${index}`}>
          {column.checkRoles ? (
            <RequireRoles roles={column.checkRoles}>{renderHeader(column, index)}</RequireRoles>
          ) : (
            renderHeader(column, index)
          )}
        </React.Fragment>
      ))}
      <Popover
        open={menuOpen}
        anchorEl={menuAnchorEl}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem
          onClick={() => {
            moveSelectedColumn('left');
          }}
          disabled={selectedColumnIndex <= 0}
        >
          <ArrowBackIcon fontSize="small" sx={{ mr: 1 }} />
          Move left
        </MenuItem>
        <MenuItem
          onClick={() => {
            moveSelectedColumn('right');
          }}
          disabled={selectedColumnIndex < 0 || selectedColumnIndex >= manageableVisibleColumns.length - 1}
        >
          <ArrowForwardIcon fontSize="small" sx={{ mr: 1 }} />
          Move right
        </MenuItem>
        <MenuItem
          onClick={() => {
            hideSelectedColumn();
          }}
        >
          <VisibilityOffIcon fontSize="small" sx={{ mr: 1 }} />
          Hide
        </MenuItem>
      </Popover>
    </>
  );
}

export default RenderColumns;
